import iconImage from 'assets/images/icon.png';
import Icon from 'components/atoms/Icon';
import IconBackground from 'components/atoms/IconBackground';
import VStack from 'components/atoms/stack/VStack';
import ThemedBlock from 'components/atoms/ThemedBlock';
import ThemedLayout from "components/atoms/ThemedLayout";
import ThemedPressable from 'components/atoms/ThemedPressable';
import ThemedSafeAreaComponentTop from "components/atoms/ThemedSafeAreaTop";
import _ from 'lodash';
import React, { useEffect, useState } from "react";
import { StyleSheet, Image, View } from "react-native";
import * as Animatable from 'react-native-animatable';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useDispatch } from "react-redux";
import QRcode from 'scenes/authentication/components/QRcode';
import Token, { TokenStatus } from 'scenes/authentication/components/Token';
import AppUpdater from "services/app-updater";
import { resetAuthentication } from 'services/redux/authentication/actions';
import { resetCart } from 'services/redux/cart/actions';
import { removeSettings, resetData } from "services/redux/data/actions";
import { resetManagement } from 'services/redux/management/actions';
import UserService from 'services/user';
import { layoutStyles } from 'styles/layout';
import { textProps } from 'styles/text';
import { ThemedShared } from "styles/themed";
import Modal from 'theme/Modal';
import Spinner from "theme/Spinner";
import Text from "theme/Text";
import CacheStorage from "utils/cache/CacheStorage";
import { colorList } from "utils/colors";
import { IconSystemConstants } from 'utils/constants/IconNameConstants';
import { settingsConstants } from 'utils/constants/settings-constants';
import { useIsSmallDevice } from 'utils/helper-device';
import HelperPlatform from 'utils/helper-platform';
import HelperSetting from 'utils/helper-setting';
import HelperString from 'utils/helper-string';
import HelperVersion from "utils/helper-version";
import HelperAlerts from 'utils/HelperAlerts';
import HelperFileCache from 'utils/HelperFileCache';
import i18n from "utils/i18n";
import Domain, { DomainStatus } from './components/Domain';
import Email, { EmailStatus } from './components/Email';
import { styles } from "./styles/styles";

const AUTHENTICATION_MODE = {
	QR: 'qr',
	TOKEN: 'token',
}

const AuthenticationDomainScene = () => {
	
	const dispatch = useDispatch()
	const isSmallDevice = useIsSmallDevice()
	
	const insets = useSafeAreaInsets();
	
	const [ mode, setMode ] = useState( '' );
	const [ domain, setDomain ] = useState( '' );
	const [ domainStatus, setDomainStatus ] = useState( DomainStatus.UNKOWN );
	const [ mailStatus, setMailStatus ] = useState( EmailStatus.UNKOWN );
	const [ tokenStatus, setTokenStatus ] = useState( TokenStatus.UNKOWN );
	const [ employeeName, setEmployeeName ] = useState( '' );
	
	const [ mail, setMail ] = useState( '' );
	const [ tokenSentToMethod, setTokenSentToMethod ] = useState( null );
	const [ updating, setUpdating ] = useState( false );
	const [ showTestFlightModal, setShowTestFlightModal ] = useState( true );
	
	const [ webAuthenticationMode, setWebAuthenticationMode ] = useState( AUTHENTICATION_MODE.QR );
	
	const logInQrCodeEnabled = HelperSetting.isYes( settingsConstants.LOG_IN_QR_CODE )
	
	const onDomainChange = ( domain ) => {
		setDomain( domain );
		setMail( '' );
		setMailStatus( EmailStatus.UNKOWN );
	}
	
	const onDomainStatusChange = ( status ) => {
		setDomainStatus( status );
	}
	
	const onModeChange = ( mode ) => {
		setMode( mode );
	}
	
	const onMailChange = ( email ) => {
		setMail( email );
	}
	
	const onMailStatusChange = ( status, sentToMethod ) => {
		setMailStatus( status );
		setTokenSentToMethod( sentToMethod );
	}
	
	const onTokenStatusChange = ( status ) => {
		setTokenStatus( status );
	}
	
	const onNameChange = ( name ) => {
		if( _.includes( name, ' ' ) ) {
			name = name.substr( 0, name.indexOf( " " ) );
		}
		
		setEmployeeName( name );
	}
	
	const logOut = () => {
		UserService.logout({
			restart: false,
			resetData: () => {
				return dispatch(resetData());
			},
			resetManagement: () => {
				return dispatch(resetManagement());
			},
			resetAuthentication: () => {
				return dispatch(resetAuthentication());
			},
			resetCart: () => {
				return dispatch(resetCart());
			},
		});
	}
	
	const checkForUpdates = ( warn = true ) => {
		const appUpdater = new AppUpdater();
		
		appUpdater
			.check()
			.then( () => {
				if( appUpdater.updateIsAvailable ) {
					CacheStorage.clearCache( { restart: false } );
					HelperFileCache.drop();
					
					appUpdater.createAlert( {
						callbackStart: setUpdating.bind( this, true ),
						callbackSuccess: setUpdating.bind( this, false ),
						callbackError: setUpdating.bind( this, false )
					})
				} else if( warn ){
					HelperAlerts.alert(
						i18n.t('update.up_to_date.title'),
						i18n.t('update.up_to_date.message'),
					);
				}
			});
		
	}
	
	useEffect(() => {
		HelperFileCache.drop();
		
		dispatch( removeSettings() )
		
		// Force reset all
		logOut();
		
		// Check for updates
		checkForUpdates( false );
	}, [] );
	
	useEffect( () => {
		setWebAuthenticationMode( logInQrCodeEnabled ? AUTHENTICATION_MODE.QR : AUTHENTICATION_MODE.TOKEN )
	}, [ logInQrCodeEnabled ] )
	
	const authenticationButtons = [];
	if( logInQrCodeEnabled ) {
		authenticationButtons.push(
			<View
				key={ HelperString.hash('authentication-domain-qr') }
				style={ styles.authenticationModeButtonContainer}>
				
				<ThemedPressable onPress={ setWebAuthenticationMode.bind( this, AUTHENTICATION_MODE.QR )}>
					<View style={ [ styles.authenticationModeButton, webAuthenticationMode === AUTHENTICATION_MODE.QR ? styles.authenticationModeButtonActive : null ] }>
						<Icon icon={ IconSystemConstants.QRCODE } style={ staticStyles.iconColorPrimaryFontSize24 }/>
						<Text>{ i18n.t('page.authentication.domain.authentication_mode.qr') }</Text>
					</View>
				</ThemedPressable>
			</View>
		)
	}
	
	authenticationButtons.push(
		<View
			key={ HelperString.hash('authentication-domain-token') }
			style={ styles.authenticationModeButtonContainer}>
			
			<ThemedPressable onPress={ setWebAuthenticationMode.bind( this, AUTHENTICATION_MODE.TOKEN )}>
				<View style={ [ styles.authenticationModeButton, webAuthenticationMode === AUTHENTICATION_MODE.TOKEN ? styles.authenticationModeButtonActive : null ] }>
					<View>
						<Text style={ styles.tokenIconText}>****</Text>
					</View>
					<Text>{ i18n.t('page.authentication.domain.authentication_mode.token') }</Text>
				</View>
			</ThemedPressable>
		</View>
	)
	
	return (
		<ThemedSafeAreaComponentTop
			top={ false }>
			
			<ThemedLayout
				showBackButton={ false }
				topNavigation={ false }
				enableScrollView={ true }
				style={staticStyles.themedLayoutPaddingVertical0PaddingHorizontal0}>
				
				<IconBackground
					containerStyle={ layoutStyles.flexOne }
					iconImageStyle={ layoutStyles.flexOne }
					opacity={ 0.1 }
					linearGradientColours={ [
						colorList.PAGE.primary,
						colorList.ORANGE.subtle,
						colorList.PAGE.primary,
						colorList.WHITE.primary
					] }>
					
					<View
						style={[
							styles.outerContainer,
							isSmallDevice ? styles.outerContainerMobile : null,
							{ paddingTop: insets.top },
						]}>

						<View
							style={ styles.headerContainer }>

							<Image
								source={ iconImage }
								style={ styles.headerImage }
							/>

							<Text
								category="h1"
								appearance={ "alternative" }
								style={ [ styles.headerTitle ] }>
								
								{ i18n.t('page.authentication.domain.title') }
							</Text>

							<Text style={ styles.headerSubtitle }>
								{ i18n.t('page.authentication.domain.subtitle') }
							</Text>
						</View>
						
						{ tokenStatus !== TokenStatus.VERIFIED && (
							<View style={ [ styles.container ]  }>
								<VStack 
									style={ styles.itemsContainer }>

									<ThemedBlock mediumPadding style={[ styles.authenticationCard, isSmallDevice ? styles.authenticationCardMobile : null ]}>
										<VStack>
											<View>
												<Text category="h4" style={ [ styles.headerTitleDark ] }>{ i18n.t('page.authentication.domain.domain') }</Text>
												<Text category="c1" style={ [ styles.headerTitleDark ] }>{ i18n.t('page.authentication.domain.domain_hint') }</Text>
											</View>

											<Domain
												key="domain_input"
												onDomainChange={ onDomainChange.bind( this ) }
												onDomainStatusChange={ onDomainStatusChange.bind( this ) }
												onModeChange={ onModeChange.bind( this ) }
											/>
										</VStack>
									</ThemedBlock>
									
									{ domainStatus === DomainStatus.VERIFIED && (
										<VStack>
											{ HelperPlatform.isNotWeb() ? (
												<ThemedBlock mediumPadding style={[ styles.authenticationCard, isSmallDevice ? styles.authenticationCardMobile : null ]}>
													<VStack>
														<Text category="h4" style={ [ styles.headerTitleDark ] }>{ i18n.t('page.authentication.domain.email') }</Text>
														<Email
															key="email_input"
															onMailChange={ onMailChange.bind( this ) }
															onMailStatusChange={ onMailStatusChange.bind( this ) }
															domainStatus={ domainStatus }
														/>
													</VStack>
												</ThemedBlock>
											) : (
												<ThemedBlock style={[ styles.webBlock, styles.authenticationCard, isSmallDevice ? styles.authenticationCardMobile : null ]} mediumPadding>
													<VStack>
														<Text category="h4" style={ [ styles.headerTitleDark ] }>{ i18n.t('page.authentication.domain.authentication_mode_title') }</Text>
													
														{ _.size( authenticationButtons ) > 1 && (
															<View style={ styles.itemsContainer }>
																<View style={ styles.authenticationModeContainer} >
																	{ authenticationButtons }
																</View>
															</View>
														)}
														
														<View style={ styles.itemsContainer }>
															{ webAuthenticationMode === AUTHENTICATION_MODE.QR ? (
																<View
																	style={[ styles.qrContainer, isSmallDevice ? styles.qrContainerMobile : null ]}>

																	<View
																		style={[ styles.qrItem, styles.qrCodeItem, isSmallDevice ? styles.qrItemMobile : null ]}>

																		<QRcode
																			onTokenStatusChange={ onTokenStatusChange.bind( this ) }
																			onNameChange={ onNameChange.bind( this ) }
																		/>
																	</View>

																	<View
																		style={[
																			styles.qrItem,
																			isSmallDevice ? styles.qrItemMobile : null,
																			isSmallDevice ? styles.qrExplanationMobile : null,
																		]}>

																		<View style={ styles.numberedList }>
																			<View style={ styles.numberedListNumber}>
																				<Text>1.</Text>
																			</View>
																			<View style={ styles.numberedListText }>
																				<Text>{i18n.t( 'pages.authentication.qr-code.explanation-1')}</Text>
																			</View>
																		</View>
																		<View style={ styles.numberedList }>
																			<View style={ styles.numberedListNumber}>
																				<Text>2.</Text>
																			</View>
																			<View style={ styles.numberedListText }>
																				<Text>{i18n.t( 'pages.authentication.qr-code.explanation-2')}</Text>
																			</View>
																		</View>
																		<View style={ styles.numberedList }>
																			<View style={ styles.numberedListNumber}>
																				<Text>3.</Text>
																			</View>
																			<View style={ styles.numberedListText }>
																				<Text>{i18n.t( 'pages.authentication.qr-code.explanation-3')}</Text>
																			</View>
																		</View>
																	</View>
																</View>
															) : (
																<VStack>
																	<Text category="h4" style={ [ styles.headerTitleDark ] }>{ i18n.t('page.authentication.domain.email') }</Text>
																	<Email
																		key="email_input"
																		onMailChange={ onMailChange.bind( this ) }
																		onMailStatusChange={ onMailStatusChange.bind( this ) }
																		domainStatus={ domainStatus }
																	/>
																</VStack>
															)}
														</View>
													</VStack>
												</ThemedBlock>
											)}
										</VStack>
									)}
									
									{ mailStatus === EmailStatus.VERIFIED && (
										<ThemedBlock style={[ styles.webBlock, styles.authenticationCard, isSmallDevice ? styles.authenticationCardMobile : null ]} mediumPadding>
											<VStack>
												<Text category="h4" style={ [ styles.headerTitleDark ] }>{ i18n.t('page.authentication.domain.token') }</Text>
												<Token
													tokenSentToMethod={ tokenSentToMethod }
													mail={ mail }
													domain={ domain }
													onTokenStatusChange={ onTokenStatusChange.bind( this ) }
													onNameChange={ onNameChange.bind( this ) }
												/>
											</VStack>
										</ThemedBlock>
									)}
								</VStack>
							</View>
						)}
					</View>
								
					{ tokenStatus !== TokenStatus.VERIFIED && (
						<VStack>
							<Text style={ styles.modVersion}>{i18n.t( 'app_name' )} { HelperVersion.get() }</Text>

							{ HelperPlatform.isNotWeb() && (
								<VStack>
									{ !updating ? (
										<ThemedPressable onPress={ checkForUpdates }>
											<Text style={ styles.modVersionUpdate}>{ i18n.t('update.check') }</Text>
										</ThemedPressable>
									) : (
										<Modal
											visible={ true }
											backdropStyle={ ThemedShared.backdrop }>
											
											<Spinner
												size='large'
												color={ colorList.RED.primary }
											/>
										</Modal>
									)}
								</VStack>
							)}
						</VStack>
					)}
				</IconBackground>

				{ tokenStatus === TokenStatus.VERIFIED && (
					<View style={ styles.successContainer}>
						<View style={ styles.successTextContainer}>
							
							<Animatable.View animation={ "fadeIn"} duration={ 1000 } >
								<Animatable.View animation={ "jello" } delay={ 800 } >
									<Icon icon={ IconSystemConstants.SUCCESS } style={ [ styles.iconSuccess ] } />
								</Animatable.View>
							</Animatable.View>
						
							<Text
								{ ...textProps.fit }
								category={"h1"}
								style={ styles.textSuccess }>
								
								{ i18n.t( 'page.authentication.domain.welcome', { name: employeeName } ) }
							</Text>
						</View>
					</View>
				)}
			</ThemedLayout>
		</ThemedSafeAreaComponentTop>
	);
}

const staticStyles = StyleSheet.create({
	iconColorPrimaryFontSize24: {
		color: colorList.ORANGE.primary,
		fontSize: 24,
	},
	themedLayoutPaddingVertical0PaddingHorizontal0: {
		paddingVertical: 0,
		paddingHorizontal: 0,
	},
});

export default AuthenticationDomainScene;
